import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Product } from '../types/common';

interface CheckboxItem {
    id: string;
    name: string;
    checked: boolean;
}

type CheckboxFillerType = { id?: string; name: string; url: string };
const normalizeSlug = (v?: string | null) => (v ?? '').toString().trim().toLowerCase();

const useFilterByFillerType = (products: Product[], checkboxTypesOfFiller: CheckboxFillerType[]) => {
    const [checkedFilterItems, setCheckedFilterItems] = useState<string[]>([]);
    const { url } = useParams<{ url: string }>();
    const routeProducts = useMemo(() => {
        if (!url || url === 'discounts') return products;
        return products.filter((p) => (p as any)?.category?.url === url);
    }, [products, url]);
    useEffect(() => {
        setCheckedFilterItems([]);
    }, [url]);
    const checkFilterItem = (slug: string) => {
        const s = normalizeSlug(slug);
        setCheckedFilterItems((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };

    const getFillerSlug = (p: any) => {
        const ft = p?.fillerType;
        if (ft == null) return '';
        if (typeof ft === 'string') return normalizeSlug(ft);
        return normalizeSlug(ft?.url ?? ft?.slug ?? p?.fillerTypeUrl ?? p?.fillerTypeSlug);
    };
    const productFillerTypeSlugs = useMemo(() => {
        const set = new Set<string>();
        for (const p of routeProducts) {
            const s = getFillerSlug(p);
            if (s) set.add(s);
        }
        return set;
    }, [routeProducts]);

    const checkboxItems: CheckboxItem[] = useMemo(() => {
        return checkboxTypesOfFiller
            .map((fillerType) => {
                const slug = normalizeSlug(fillerType.url);
                if (!slug) return null;
                return { slug, name: fillerType.name };
            })
            .filter(Boolean)
            .filter((b) => productFillerTypeSlugs.has((b as any).slug))
            .map((b) => ({
                id: (b as any).slug,
                name: (b as any).name,
                checked: checkedFilterItems.includes((b as any).slug),
            }));
    }, [checkboxTypesOfFiller, checkedFilterItems, productFillerTypeSlugs]);
    const productsTorender =
        checkedFilterItems.length > 0
            ? routeProducts.filter((p) => {
                  const slug = getFillerSlug(p);
                  return slug ? checkedFilterItems.includes(slug) : false;
              })
            : routeProducts;
    return {
        checkedFilterItems,
        checkFilterItem,
        productsTorender,
        checkboxItems,
    };
};

export default useFilterByFillerType;
